import { Button, Card, Flex } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { HubspotFieldMappings } from '../custom-components/HubspotFieldMappings';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext } from '../global-context';
import { usePermission } from '../hooks/usePermission';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import './Integrations.scss';

export function IntegrationHubspot() {
  const globalContext = useContext(GlobalContext);
  const { hasPermission } = usePermission();
  const canManageConnection = hasPermission('crm-connection:manage');
  const canEditFieldMappings = hasPermission('crm-field-mappings:edit');
  const [hubspotConnectionStatus, setHubspotConnectionStatus] = useState<Edulead.EduleadV4Integration | null>(null);
  const [isLoadingHubspotStatus, setIsLoadingHubspotStatus] = useState(true);
  const [isConnectingToHubspot, setIsConnectingToHubspot] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fieldMappingsKey, setFieldMappingsKey] = useState(0);

  useEffect(() => {
    General.setTitle(document, 'HubSpot Integration', globalContext);
  }, [globalContext]);

  useEffect(() => {
    if (globalContext?.authInfo?.teamId) {
      loadConnectionStatus();
    }
  }, [globalContext.authInfo?.teamId]);

  useEffect(() => {
    handleHubspotCallback();
  }, []);

  async function loadConnectionStatus(showToast = false) {
    try {
      setIsRefreshing(true);
      setIsLoadingHubspotStatus(true);

      const response = await eduleadV4.integrations.get({ id: 'hubspot' });
      setHubspotConnectionStatus(response.integration ?? null);

      if (showToast) {
        toast.success('Connection status refreshed');
      }
    } catch (error) {
      console.error('Failed to load HubSpot connection status:', error);
      if (showToast) {
        toast.error('Failed to load HubSpot connection status');
      }
    } finally {
      setIsLoadingHubspotStatus(false);
      setIsRefreshing(false);
    }
  }

  // The API asks HubSpot for the contacts and companies scopes; the deals scopes served deal
  // syncs, which nobody reaches any more (api/v4/integrations.md §11)
  async function connectToHubspot() {
    try {
      setIsConnectingToHubspot(true);
      const response = await eduleadV4.integrations.createAuthorization({
        integration_id: 'hubspot',
        request_id: crypto.randomUUID(),
        body: {},
      });
      window.open(response.integration_authorization?.authorization_url);
      setIsConnectingToHubspot(false);
    } catch (error) {
      toastV4Error(error, 'Failed to initiate HubSpot connection');
      setIsConnectingToHubspot(false);
    }
  }

  async function disconnectFromHubspot() {
    try {
      if (window.confirm('Are you sure you want to disconnect from HubSpot?')) {
        await eduleadV4.integrations.delete({ id: 'hubspot' });
        toast.success('Successfully disconnected from HubSpot');
        await loadConnectionStatus();
      }
    } catch (error) {
      toastV4Error(error, 'Failed to disconnect from HubSpot');
    }
  }

  async function handleHubspotCallback() {
    // Match both legacy (/settings/hubspot/callback) and new (/settings/integrations/hubspot/callback) paths
    if (!window.location.pathname.includes('/hubspot/callback')) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      try {
        setIsConnectingToHubspot(true);
        await eduleadV4.integrations.connect({ id: 'hubspot', code, state });

        toast.success('Successfully connected to HubSpot!');

        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.toString());

        await loadConnectionStatus();
        // Remount the field mappings: they mounted on the status read before the connect finished
        // (not connected, so hidden) and do not reload on their own
        setFieldMappingsKey(prev => prev + 1);
      } catch (error) {
        toastV4Error(error, 'Failed to complete HubSpot connection');
      } finally {
        setIsConnectingToHubspot(false);
      }
    }
  }

  async function handleFieldMappingsSaved() {
    // Refresh the integration (configuration status and the saved mappings), then remount the
    // field mappings component on it
    await loadConnectionStatus();
    setFieldMappingsKey(prev => prev + 1);
  }

  return (
    <GlobalContext.Consumer>
      {() => (
        <>
          <div className="content content-1000 integrations-page">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              style={{ marginTop: 20, marginBottom: 0 }}
            >
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>HubSpot Integration</h2>
              <Button
                size="small"
                className="tiny"
                onClick={() => loadConnectionStatus(true)}
                disabled={isRefreshing || isLoadingHubspotStatus}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Flex>

            {/* Connection Status Card */}
            <Card variation='outlined' style={{ fontSize: 14, marginTop: 20 }}>
              {
                isLoadingHubspotStatus ? (
                  <LoadingBars numberOfRows={6} />
                ) : (
                  <>
                    {hubspotConnectionStatus?.connected ? (
                      <div>
                        <div className="status-section">
                          <div className="connection-status">
                            <div className="status-dot connected"></div>
                            <span className="status-label">Connected to HubSpot</span>
                          </div>
                          <div className="field-mapping-status">
                            <div className={`status-dot ${hubspotConnectionStatus.field_mappings_configured ? 'configured' : 'not-configured'}`}></div>
                            <span className="status-label">
                              Field mappings {hubspotConnectionStatus.field_mappings_configured ? 'configured' : 'not configured'}
                            </span>
                          </div>
                          {(hubspotConnectionStatus.missing_required_fields?.length ?? 0) > 0 && (
                            <div style={{ marginTop: 10, marginLeft: 18 }}>
                              <div style={{ marginBottom: 5 }}>
                                Missing configuration:
                              </div>
                              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                                {hubspotConnectionStatus.missing_required_fields?.map((field, index) => (
                                  <li key={index}>
                                    {field.object_type === 'INTEGRATION_OBJECT_TYPE_CONTACT' ? 'Contact' : 'Company'}: {field.label}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <div style={{ marginBottom: 15 }}>
                          <div style={{ marginBottom: 5 }}>
                            <span style={{ display: 'inline-block', width: 150 }}>Account ID:</span>
                            {hubspotConnectionStatus.account_id}
                          </div>
                          <div style={{ marginBottom: 5 }}>
                            <span style={{ display: 'inline-block', width: 150 }}>Connected:</span>
                            {hubspotConnectionStatus.connect_time ? new Date(hubspotConnectionStatus.connect_time).toLocaleDateString() : ''}
                          </div>
                          <div style={{ marginBottom: 15 }}>
                            <span style={{ display: 'inline-block', width: 150, verticalAlign: 'top' }}>Scopes:</span>
                            <div style={{ display: 'inline-block', maxWidth: 'calc(100% - 150px)' }}>
                              {hubspotConnectionStatus.scopes?.map((scope) => (
                                <div key={scope} style={{ fontFamily: 'monospace' }}>
                                  {scope}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <span
                            data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                            data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                          >
                            <Button
                              size="small"
                              onClick={disconnectFromHubspot}
                              disabled={!canManageConnection}
                            >
                              Disconnect HubSpot
                            </Button>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 15 }}>
                          Connect your HubSpot account to sync contacts and enable CRM integration features.
                        </div>
                        <span
                          data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                          data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                        >
                          <Button
                            size="small"
                            variation="primary"
                            className='tiny'
                            onClick={connectToHubspot}
                            disabled={isConnectingToHubspot || !canManageConnection}
                          >
                            {isConnectingToHubspot ? 'Connecting...' : 'Connect to HubSpot'}
                          </Button>
                        </span>
                      </div>
                    )}
                  </>
                )
              }
            </Card>

            {/* Field Mappings Section - only render after connection status is loaded */}
            {globalContext?.authInfo?.teamId && hubspotConnectionStatus && (
              <div style={{ marginTop: 30 }}>
                <HubspotFieldMappings
                  key={fieldMappingsKey}
                  teamId={globalContext.authInfo.teamId}
                  integration={hubspotConnectionStatus}
                  onSaveSuccess={handleFieldMappingsSaved}
                  readOnly={!canEditFieldMappings}
                />
              </div>
            )}
          </div>
        </>
      )}
    </GlobalContext.Consumer>
  );
}
